import React, { useEffect } from 'react';
import { Controller, set, useForm } from 'react-hook-form';
import Loader from '../../../components/loader';
import Input from '../../../components/elements/form/input';
import Button from '../../../components/elements/button';
import Icon from '../../../components/icons';
import { useDialog } from '../../../store/api/hooks/dialogHook';
import toast from 'react-hot-toast';
import { useUserApi } from '../../../store/api/hooks/userApiHook';

interface CreatePermissionFormProps {
    userModuleID?: number | string | null;
    userTypeID: number | string;
    onCreate?: () => void;
    onCancel?: () => void;
}

type FormValues = {
    userModuleID: string | null;
    userTypeID: string;
    name: string;
    url: string;
    apIs: Array<{ apiurl: string }>;
    entities: Array<{ obj: string, r: boolean, w: boolean, d: boolean }>;
    isTemplate: boolean;
    displayOrder: string;
};

const CreatePermission: React.FC<CreatePermissionFormProps> = ({ userModuleID = null, userTypeID = null, onCreate = () => { }, onCancel = () => { } }) => {
    const { control, handleSubmit, formState: { errors }, reset, getValues, setValue, watch } = useForm<FormValues>({
        defaultValues: {
            name: '',
            url: '',
            apIs: [],
            entities: [],
            isTemplate: true,
            displayOrder: `1`
        }
    });

    const { confirm } = useDialog();
    const userApi = useUserApi();
    const blankApIsRow = { apiurl: `` };
    const blankEntitiesRow = { obj: ``, r: false, w: false, d: false };

    const apIsWatcher = watch(`apIs`);
    const entitiesWatcher = watch(`entities`);

    const [showLoader, setShowLoader] = React.useState<boolean>(false);

    const addApIsResource = () => setValue('apIs', [...getValues('apIs'), { ...blankApIsRow }]);
    const addEntity = () => setValue('entities', [...getValues('entities'), { ...blankEntitiesRow }]);

    const init_page = async () => {
        if (!userModuleID) {
            //addApIsResource();
            //addEntity();
        } else {
            setShowLoader(true);
            let moduleresp: any = null;
            try {
                moduleresp = await new Promise((resolve, reject) => {
                    userApi.getUserModuleByID({ userModuleID: userModuleID }, (resp: any) => {
                        resolve(resp);
                    }, (error: any) => {
                        reject(error);
                    });
                });
            } catch (error) {
                console.error('Error fetching user module:', error);
                moduleresp = null;
            } finally {
                setShowLoader(false);
            }
            if (moduleresp?.userModuleID === userModuleID) {
                setValue('name', moduleresp.name ?? '');
                setValue('url', moduleresp.url ?? '');
                setValue('apIs', Array.isArray(moduleresp.apIs) ? moduleresp.apIs : []);
                setValue('entities', Array.isArray(moduleresp.entities) ? moduleresp.entities : []);
                setValue('isTemplate', moduleresp.isTemplate);
                setValue('displayOrder', moduleresp.displayOrder || 1);
            }
        }
    };

    const onSubmit = async (data: FormValues) => {
        setShowLoader(true);
        try {
            const savemoduleresponse: any = await new Promise((resolve, reject) => {
                userApi.saveUserModule({ userModuleID: userModuleID, userTypeID: userTypeID, name: data.name, url: data.url, isTemplate: true, displayOrder: data.displayOrder }, resolve, reject);
            })
            if (savemoduleresponse && savemoduleresponse?.successful && +savemoduleresponse?.code === 200 && savemoduleresponse?.returnID) {
                const saveentityresponse: any = await new Promise((resolve, reject) => {
                    userApi.saveUserModuleApiEntity({
                        userModuleID: +savemoduleresponse.returnID,
                        apIs: data.apIs,
                        entities: data.entities
                    }, resolve, reject);
                });
                if (saveentityresponse && saveentityresponse?.successful && +saveentityresponse?.code === 200) {
                    toast.success(savemoduleresponse?.message || 'Permission module has been saved successfully.');
                    onCreate();
                } else {
                    toast.error(saveentityresponse?.message || 'Failed to save permission module entities, please try again');
                }
            } else {
                toast.error(savemoduleresponse?.message || 'Failed to save permission module, please try again');
            }
            setShowLoader(false);
        } catch (error) {
            setShowLoader(false);
            toast.error('Network error, unable to save permission module');
        }
    };

    useEffect(() => {
        init_page();
    }, []);

    return (
        <div className={`relative h-full flex flex-col`}>
            {showLoader && (
                <div className={`absolute p-8 w-full h-full z-[2px] bg-white bg-opacity-30`}>
                    <Loader />
                </div>
            )}
            <form onSubmit={handleSubmit(onSubmit)} className={`${showLoader ? `hidden` : `flex flex-col h-full`}`} noValidate>
                <div className="flex-1 overflow-auto">
                    <div className={`space-y-4`}>
                        {/* Name Field */}
                        <Controller
                            name={`name`}
                            control={control}
                            rules={{ required: `Module Name is required` }}
                            render={({ field: { value, onChange } }) => (
                                <Input
                                    label={`Module Name`}
                                    type='text'
                                    value={value}
                                    onChange={onChange}
                                    required={true}
                                    error={errors.name?.message as any}
                                    placeholder='Enter Module Name'
                                />
                            )}
                        />
                        {/* URL Field */}
                        <Controller
                            name='url'
                            control={control}
                            rules={{ required: 'URL is required' }}
                            render={({ field }) => (
                                <Input
                                    label='URL'
                                    type='text'
                                    {...field}
                                    required
                                    error={errors.url?.message as any}
                                    placeholder='Enter Module URL (e.g., /users/permissions)'
                                />
                            )}
                        />
                        <div className={`flex gap-4 items-center`}>
                            {/* Sort Field */}
                            <Controller
                                name='displayOrder'
                                control={control}
                                rules={{ required: 'Display Order is required' }}
                                render={({ field }) => (
                                    <Input
                                        label='Display Order'
                                        type='text'
                                        {...field}
                                        required
                                        error={errors.displayOrder?.message as any}
                                        placeholder='Enter Module Display Order'
                                    />
                                )}
                            />
                            <div className={`my-6`}>
                                <label className='flex items-center gap-3 mt-6'>
                                    <Controller name={`isTemplate`} control={control} render={({ field }) => (
                                        <input
                                            type='checkbox'
                                            checked={field.value}
                                            onChange={e => field.onChange(e.target.checked)}
                                            onBlur={field.onBlur}
                                            name={field.name}
                                            ref={field.ref}
                                        />
                                    )} /> Is Template
                                </label>
                            </div>                            
                        </div>

                        {/* Dynamic API Resource Section */}
                        <div className='mt-6'>
                            <div className={`flex items-center justify-between bg-slate-100 px-2 py-2 rounded mb-2`}>
                                <h4 className='text-[0.938rem] font-semibold mb-0'>API Resource</h4>
                                <a className={`border border-[#2b5a2e] pr-3 pl-2 py-1 rounded text-sm font-normal flex items-center gap-1 cursor-pointer`} onClick={() => addApIsResource()}><Icon name={`plus`} size={1} /> Add More</a>
                            </div>
                            {apIsWatcher.map((urlfield, index) => (
                                <div key={`urlfield-${index}`} className='flex gap-2 items-center mb-2'>
                                    <Controller
                                        name={`apIs.${index}.apiurl`}
                                        control={control}
                                        rules={{ required: 'API URL is required' }}
                                        render={({ field }) => (
                                            <Input
                                                label={``}
                                                type='text'
                                                className='!my-0 mb-2 grow'
                                                {...field}
                                                required
                                                error={errors.apIs?.[index]?.apiurl?.message as any}
                                                placeholder='Enter API URL (e.g., /users/permissions)'
                                            />
                                        )}
                                    />
                                    <a className={`flex items-center gap-1 bg-red-600 text-white text-sm font-normal py-1 pl-1 pr-1.5 mt-2 rounded cursor-pointer`} onClick={() => {
                                        confirm({
                                            type: 'warn',
                                            title: 'Confirmation',
                                            message: 'Are you sure you want to remove access from this API URL?',
                                            onConfirm: () => {
                                                const currentApis = getValues(`apIs`);
                                                currentApis.splice(index, 1);
                                                setValue(`apIs`, currentApis);
                                            }
                                        });

                                    }}>
                                        <Icon name='delete' size={0.8} /> Remove
                                    </a>
                                </div>
                            ))}
                            {apIsWatcher.length < 1 && (
                                <p className='text-sm text-gray-500 h-14 border border-gray-300 bg-gray-50 flex items-center justify-center font-normal rounded'>No API URLs added</p>
                            )}
                        </div>

                        {/* Dynamic Entities Section */}
                        <div className='mt-6'>
                            <div className={`flex items-center justify-between bg-slate-100 px-2 py-2 rounded mb-2`}>
                                <h4 className='text-[0.938rem] font-semibold mb-0'>Entities</h4>
                                <a className={`border border-[#2b5a2e] pr-3 pl-2 py-1 rounded text-sm font-normal flex items-center gap-1 cursor-pointer`} onClick={() => addEntity()}><Icon name={`plus`} size={1} /> Add More</a>
                            </div>
                            {entitiesWatcher.map((entityField, idx) => (
                                <div key={`entity-${idx}`} className='flex gap-5 items-center mb-2'>
                                    <Controller
                                        name={`entities.${idx}.obj`}
                                        control={control}
                                        rules={{ required: 'Entity Name is required' }}
                                        render={({ field }) => (
                                            <Input
                                                label={``}
                                                type='text'
                                                {...field}
                                                required
                                                className='!my-0 mb-2 grow'
                                                error={errors.entities?.[idx]?.obj?.message as any}
                                                placeholder='Enter Entity Name'
                                            />
                                        )}
                                    />
                                    <div className='flex items-center gap-4 mt-2'>
                                        <label className='flex items-center gap-3'>
                                            <Controller name={`entities.${idx}.r`} control={control} render={({ field }) => (
                                                <input
                                                    type='checkbox'
                                                    checked={field.value}
                                                    onChange={e => field.onChange(e.target.checked)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    ref={field.ref}
                                                />
                                            )} /> Read
                                        </label>
                                        <label className='flex items-center gap-3'>
                                            <Controller name={`entities.${idx}.w`} control={control} render={({ field }) => (
                                                <input
                                                    type='checkbox'
                                                    checked={field.value}
                                                    onChange={e => field.onChange(e.target.checked)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    ref={field.ref}
                                                />
                                            )} /> Write
                                        </label>
                                        <label className='flex items-center gap-3'>
                                            <Controller name={`entities.${idx}.d`} control={control} render={({ field }) => (
                                                <input
                                                    type='checkbox'
                                                    checked={field.value}
                                                    onChange={e => field.onChange(e.target.checked)}
                                                    onBlur={field.onBlur}
                                                    name={field.name}
                                                    ref={field.ref}
                                                />
                                            )} /> Delete
                                        </label>
                                    </div>
                                    <a className={`flex items-center gap-1 bg-red-600 text-white text-sm font-normal py-1 pl-1 pr-1.5 mt-2 rounded cursor-pointer`} onClick={() => {
                                        confirm({
                                            type: 'warn',
                                            title: 'Confirmation',
                                            message: 'Are you sure you want to remove this Entity?',
                                            onConfirm: () => {
                                                const currentEntities = getValues(`entities`);
                                                currentEntities.splice(idx, 1);
                                                setValue(`entities`, currentEntities);
                                            }
                                        });

                                    }}>
                                        <Icon name='delete' size={0.8} /> Remove
                                    </a>
                                </div>
                            ))}
                            {entitiesWatcher.length < 1 && (
                                <p className='text-sm text-gray-500 h-14 border border-gray-300 bg-gray-50 flex items-center justify-center font-normal rounded'>No Entities added</p>
                            )}
                        </div>

                    </div>
                </div>

                <div className={`flex justify-end gap-2 flex-shrink-0 pt-4 border-t border-gray-200 mt-6`}>
                    <Button type='button' theme='secondary' onClick={() => {
                        reset();
                        onCancel();
                    }}>
                        Cancel
                    </Button>
                    <Button type='submit'>{userModuleID ? 'Update' : 'Save'}</Button>
                </div>
            </form>
        </div>
    );
};

export default CreatePermission;
